import { DateTime, WeekdayNumbers } from "luxon";
import { Calendar, CalendarRangeValue } from "../../Calendar/Calendar";
import { useEffect, useMemo, useRef } from "react";
import { usePrevious } from "../../../internal/hooks";
import { IanaZone } from "../../../types";
import { TimezoneMessage } from "../../DateFieldSingle/internal/TimezoneMessage";
import { isoToUtcDate } from "../../DateFieldSingle/internal/utils";

export type DateFieldRangeCalendarProps = {
  startDate: DateTime | null;
  endDate: DateTime | null;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  onSelection: ({
    startDate,
    endDate,
  }: {
    startDate: DateTime | null;
    endDate: DateTime | null;
  }) => void;
  minDate?: DateTime | null;
  maxDate?: DateTime | null;
  unavailable?: {
    dates?: DateTime[];
    daysOfWeek?: WeekdayNumbers[];
  };
  timezone?: IanaZone;
};

export const DateFieldRangeCalendar = ({
  startDate,
  endDate,
  onKeyDown,
  onSelection,
  minDate,
  maxDate,
  unavailable,
  timezone: timezoneProp,
}: DateFieldRangeCalendarProps) => {
  const previousStartDate = usePrevious(startDate);
  const previousEndDate = usePrevious(endDate);

  const calendarRef = useRef<HTMLDivElement>(null);

  const timezone: IanaZone = useMemo(() => {
    return (
      timezoneProp ??
      (Intl.DateTimeFormat().resolvedOptions().timeZone as IanaZone)
    );
  }, [timezoneProp]);

  // After the user keyboard-selects a start date, the Calendar remounts (due to
  // the key hack below). Restore focus to the start date button so the user can
  // continue navigating to pick an end date without losing keyboard context.
  // Guard: if the input currently has focus, the change came from typing — skip.
  useEffect(() => {
    if (startDate && startDate !== previousStartDate && endDate === null) {
      const startDateId = startDate.toISODate();
      if (!startDateId) return;
      // When typing, the input retains focus. When selecting via calendar keyboard,
      // the Calendar remounts and focus falls to body. Only restore in the latter case.
      // In Shadow DOM, document.activeElement is the host — traverse into the shadow
      // root to find the actual focused element.
      const root = (calendarRef.current?.getRootNode() ?? document) as
        | Document
        | ShadowRoot;
      const deepActiveEl =
        root.activeElement?.shadowRoot?.activeElement ?? root.activeElement;
      if (deepActiveEl?.tagName === "INPUT") return;
      requestAnimationFrame(() => {
        calendarRef.current
          ?.querySelector<HTMLButtonElement>(`button[id$="${startDateId}"]`)
          ?.focus();
      });
    }
  }, [startDate, endDate, previousStartDate]);

  const handleCalendarSelection = (data: { value: CalendarRangeValue }) => {
    if (!data.value) return;
    const calStartDate = data.value.start
      ? isoToUtcDate(data.value.start)
      : null;
    const calEndDate = data.value.end ? isoToUtcDate(data.value.end) : null;
    onSelection({
      startDate: calStartDate,
      endDate: calEndDate,
    });
  };

  const defaultFocusedDate = useMemo(() => {
    const todayISODate = DateTime.now().setZone(timezone).toISODate();
    // Nothing selected, focus today
    if (!startDate && !endDate) return todayISODate;
    if (!startDate) return endDate?.toISODate();
    if (!endDate) return startDate?.toISODate();
    if (endDate && !previousEndDate?.equals(endDate)) {
      return endDate.toISODate();
    } else if (startDate && !previousStartDate?.equals(startDate)) {
      return startDate.toISODate();
    }

    if (endDate) return endDate.toISODate();
    if (startDate) return startDate.toISODate();
    return todayISODate;
  }, [previousStartDate, previousEndDate, startDate, endDate, timezone]);

  return (
    <>
      <Calendar
        ref={calendarRef}
        // Crappy hack because the Calendar does not update when the value changes.
        key={`${startDate?.toISODate()}-${endDate?.toISODate()}`}
        range={true}
        onKeyDown={onKeyDown}
        defaultFocusedDate={defaultFocusedDate}
        value={{
          start: startDate?.toISODate() || undefined,
          end: endDate?.toISODate() || undefined,
        }}
        onSelection={handleCalendarSelection}
        defaultTimeZone={timezone ?? "UTC"}
        minDate={minDate?.toISODate() ?? undefined}
        maxDate={maxDate?.toISODate() ?? undefined}
        unavailable={
          unavailable
            ? {
                dates: unavailable.dates?.map((d) => d.toISODate() ?? ""),
                daysOfWeek: unavailable.daysOfWeek,
              }
            : undefined
        }
        _disableAutofocus
        _disableFocus
      />
      <TimezoneMessage timezone={timezone} />
    </>
  );
};
